/**
 * Dashboard JSON serialization.
 *
 * Two distinct shapes:
 *
 *   1. Meta-JSON — pure dashboard DEFINITION. No row data, no DB ids,
 *      no timestamps, no ownership. Two dashboards are considered
 *      EQUIVALENT iff their canonical Meta-JSON is byte-for-byte equal.
 *
 *   2. Data-JSON — wraps a Meta-JSON plus a snapshot of the actual
 *      data currently shown in the dashboard. This is the payload that
 *      gets attached to the data agent's system prompt so the agent
 *      knows what the user is looking at.
 *
 * Schema version constant is bumped when the canonical shape changes.
 */

export const DASHBOARD_JSON_VERSION = '2.0';

/** Normalize SQL for equality comparison: collapse whitespace, trim, lowercase keywords are NOT applied (case-sensitive). */
function normalizeSql(sql) {
  if (typeof sql !== 'string') return '';
  return sql.replace(/\s+/g, ' ').trim();
}

/** Recursively sort object keys so JSON.stringify produces a canonical form. */
function canonicalize(value) {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') {
    const out = {};
    for (const key of Object.keys(value).sort()) {
      out[key] = canonicalize(value[key]);
    }
    return out;
  }
  return value;
}

/**
 * Build the Meta-JSON for a dashboard.
 *
 * @param {object} dashboard         Full dashboard summary or row.
 * @param {object[]} items           DashboardItem rows.
 * @param {object} [filterConfig]    Optional dashboard-level filter config
 *                                   { dashboardFilters, filterVisibility,
 *                                     filterDefinitions }.
 * @returns {object} Meta-JSON
 */
export function buildDashboardMeta(dashboard, items, filterConfig = {}) {
  const dash = dashboard || {};
  const safeItems = Array.isArray(items) ? items : [];

  return {
    version: DASHBOARD_JSON_VERSION,
    kind: 'dashboard.meta',
    dashboard: {
      name: dash.name || '',
      description: dash.description || null,
      is_global: !!dash.is_global,
      is_template: !!dash.is_template,
      is_public: !!dash.is_public,
      is_favorite: !!dash.is_favorite,
      show_on_front_page: !!dash.show_on_front_page,
    },
    items: safeItems.map((it) => ({
      item_type: it.item_type,
      title: it.title,
      sql_query: it.sql_query,
      chart_config: it.chart_config || null,
      grid_x: it.grid_x ?? 0,
      grid_y: it.grid_y ?? 0,
      grid_w: it.grid_w ?? 6,
      grid_h: it.grid_h ?? 4,
    })),
    filters: {
      values: filterConfig.dashboardFilters || {},
      visibility: filterConfig.filterVisibility || {},
      // User/agent-defined custom dashboard-level filters (persisted
      // server-side under Dashboard.filter_definitions). Two dashboards
      // are equivalent iff their canonical Meta-JSON \u2014 including
      // these definitions \u2014 matches.
      definitions: Array.isArray(filterConfig.filterDefinitions)
        ? filterConfig.filterDefinitions
        : [],
    },
  };
}

/**
 * Canonical (sorted-keys, normalized-SQL) form of a Meta-JSON for equality.
 * Two dashboards are EQUAL iff JSON.stringify(canonical(metaA)) === ...(metaB).
 */
export function canonicalDashboardMeta(meta) {
  if (!meta) return null;
  // Normalize SQL whitespace per item so cosmetic reformatting doesn't change identity.
  const normalized = {
    ...meta,
    items: (meta.items || []).map((it) => ({
      ...it,
      sql_query: normalizeSql(it.sql_query),
    })),
  };
  return canonicalize(normalized);
}

/**
 * Stable string hash of a Meta-JSON suitable for equality checks / cache keys.
 */
export function dashboardMetaSignature(meta) {
  const canonical = canonicalDashboardMeta(meta);
  return canonical ? JSON.stringify(canonical) : '';
}

/**
 * True iff two dashboards have the same Meta-JSON (after canonicalization).
 */
export function dashboardMetaEquals(metaA, metaB) {
  return dashboardMetaSignature(metaA) === dashboardMetaSignature(metaB);
}

/**
 * Build the Data-JSON to attach to the agent's system prompt.
 *
 * The Data-JSON embeds the full Meta-JSON plus a per-item data snapshot
 * (columns, row count, sample rows). Sample rows are taken from the
 * client-side data cache. The agent (server-side) will cap sample_rows
 * to `dashboard.context_row_limit` from agent_config, so the frontend
 * sends whatever it has cached (already preview-sized).
 *
 * @param {object} meta              Meta-JSON from buildDashboardMeta().
 * @param {object[]} items           Same items array used to build meta
 *                                   (needed to map titles -> ids -> cache).
 * @param {(itemId:any) => any} getCachedData
 *                                   Reader returning { columns, rows } or null.
 * @param {object} [opts]
 * @param {string|null} [opts.focusedComponentTitle]
 * @param {string|null} [opts.activeDashboardId]
 * @param {string|null} [opts.activeTabName]
 * @param {string[]} [opts.dashboardTabs]
 * @returns {object} Data-JSON
 */
export function buildDashboardData(meta, items, getCachedData, opts = {}) {
  const safeItems = Array.isArray(items) ? items : [];
  const reader = typeof getCachedData === 'function' ? getCachedData : () => null;

  const components = safeItems.map((it, idx) => {
    const metaItem = (meta && meta.items && meta.items[idx]) || {};
    const cached = reader(it.id);
    const comp = {
      // The id is intentionally NOT part of Meta but IS part of Data so
      // the agent can reference / update specific components by id.
      id: it.id,
      title: metaItem.title ?? it.title,
      item_type: metaItem.item_type ?? it.item_type,
      sql_query: metaItem.sql_query ?? it.sql_query,
      chart_config: metaItem.chart_config ?? it.chart_config ?? null,
    };
    if (cached) {
      comp.data = {
        columns: cached.columns || [],
        row_count: Array.isArray(cached.rows) ? cached.rows.length : 0,
        // Send full cached rows; server slices to context_row_limit.
        sample_rows: Array.isArray(cached.rows) ? cached.rows : [],
      };
    } else {
      comp.data = null;
    }
    return comp;
  });

  return {
    version: DASHBOARD_JSON_VERSION,
    kind: 'dashboard.data',
    // Embed the full Meta-JSON so the agent (and consumers) can derive
    // equality / identity from the data payload without a second fetch.
    meta,
    active_dashboard_id: opts.activeDashboardId ?? null,
    active_tab_name: opts.activeTabName ?? null,
    dashboard_tabs: Array.isArray(opts.dashboardTabs) ? opts.dashboardTabs : [],
    focused_component: opts.focusedComponentTitle ?? null,
    components,
    // Back-compat shim: the agent currently reads `dashboard_open` and
    // a flat `components` list. Keep these at the top level so older
    // code paths keep working while consumers migrate to `meta`/`components`.
    dashboard_open: true,
  };
}
